<template>
  <div class="branch-form-container">
    <div class="form-wrapper">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-left">
            <h1 class="page-title">{{ isEditMode ? 'Edit Branch' : 'Register New Branch' }}</h1>
            <p class="page-subtitle">
              {{ isEditMode ? 'Update branch location and contact details for' : 'Configure a new physical outlet for' }}
              <strong class="text-dark">{{ currentTenantName }}</strong>
            </p>
          </div>
          <div class="header-actions">
            <button type="button" class="btn-secondary" @click="handleCancel">
              <i class="fas fa-arrow-left me-2"></i>Back to List
            </button>
            <button type="button" class="btn-primary" @click="handleSubmit" :disabled="isLoading">
              <span v-if="isLoading"><i class="fas fa-circle-notch fa-spin me-2"></i>Saving...</span>
              <span v-else><i :class="isEditMode ? 'fas fa-save' : 'fas fa-plus-circle'" class="me-2"></i>{{ isEditMode ? 'Update' : 'Create' }} Branch</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Error Message Banner -->
      <div v-if="errorMessage" class="error-banner">
        <div class="banner-icon">
          <i class="fas fa-exclamation-circle"></i>
        </div>
        <div class="banner-text">{{ errorMessage }}</div>
        <button class="banner-close" @click="errorMessage = ''">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Main Form Grid -->
      <div class="form-grid-layout">
        <!-- Left: Primary Details -->
        <div class="layout-main">
          <div class="form-section-card">
            <!-- Basic Identity -->
            <div class="content-block">
              <div class="block-header">
                <i class="fas fa-store"></i>
                <div class="header-info">
                  <h3>Branch Identity</h3>
                  <p>Core identifiers and display names for this location</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>Branch Code <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="branch.code" 
                    class="form-input" 
                    placeholder="e.g. BR-KRG-01"
                    :disabled="isEditMode"
                  >
                  <span v-if="isEditMode" class="field-hint">Code is immutable after registration</span>
                  <div v-if="validation.code" class="error-msg">{{ validation.code[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>Branch Name <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="branch.name" 
                    class="form-input" 
                    placeholder="e.g. Karawaci Store"
                  >
                  <div v-if="validation.name" class="error-msg">{{ validation.name[0] }}</div>
                </div>
              </div>
            </div>

            <!-- Location Details -->
            <div class="content-block">
              <div class="block-header">
                <i class="fas fa-map-marked-alt"></i>
                <div class="header-info">
                  <h3>Location Details</h3>
                  <p>Physical coordinates for customer navigation</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>City/Area <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="branch.city" 
                    class="form-input" 
                    placeholder="e.g. South Jakarta"
                    @input="branch.city = $event.target.value.replace(/[^a-zA-Z\s\-.]/g, '')"
                  >
                  <div v-if="validation.city" class="error-msg">{{ validation.city[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>State/Province <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="branch.state" 
                    class="form-input" 
                    placeholder="e.g. DKI Jakarta"
                    @input="branch.state = $event.target.value.replace(/[^a-zA-Z\s\-.]/g, '')"
                  >
                  <div v-if="validation.state" class="error-msg">{{ validation.state[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>Postcode <span class="required">*</span></label>
                  <input 
                    type="text" 
                    v-model="branch.postcode" 
                    class="form-input" 
                    placeholder="e.g. 15810"
                    @input="branch.postcode = $event.target.value.replace(/[^0-9]/g, '')"
                  >
                  <div v-if="validation.postcode" class="error-msg">{{ validation.postcode[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>Country <span class="required">*</span></label>
                  <o-select 
                    v-model="branch.country" 
                    :options="options"
                    placeholder="Select Country"
                  />
                  <div v-if="validation.country" class="error-msg">{{ validation.country[0] }}</div>
                </div>

                <div class="form-field-group full">
                  <label>Detailed Street Address</label>
                  <textarea 
                    v-model="branch.address" 
                    class="form-textarea" 
                    rows="3" 
                    placeholder="Tower name, floor, street details..."
                  ></textarea>
                  <div v-if="validation.address" class="error-msg">{{ validation.address[0] }}</div>
                </div>
              </div>
            </div>

            <!-- Contact & Channels -->
            <div class="content-block no-border">
              <div class="block-header">
                <i class="fas fa-phone-alt"></i>
                <div class="header-info">
                  <h3>Contact & Channels</h3>
                  <p>Direct communication lines for this specific branch</p>
                </div>
              </div>

              <div class="fields-grid">
                <div class="form-field-group">
                  <label>Branch Phone <span class="required">*</span></label>
                  <div class="field-prefix-group">
                    <span class="prefix-text" v-if="!unknownCountry">{{ countryCode.trim() }}</span>
                    <input 
                      type="text" 
                      v-model="branch.phone" 
                      class="form-input" 
                      placeholder="812xxx"
                      @input="branch.phone = $event.target.value.replace(/[^0-9+\-\s()]/g, '')"
                    >
                  </div>
                  <div v-if="validation.phone" class="error-msg">{{ validation.phone[0] }}</div>
                </div>

                <div class="form-field-group">
                  <label>Branch Fax</label>
                  <input 
                    type="text" 
                    v-model="branch.fax" 
                    class="form-input" 
                    placeholder="+62 21..."
                    @input="branch.fax = $event.target.value.replace(/[^0-9+\-\s()]/g, '')"
                  >
                </div>

                <div class="form-field-group">
                  <label>Branch Website</label>
                  <input 
                    type="text" 
                    v-model="branch.website" 
                    class="form-input" 
                    placeholder="https://..."
                  >
                </div>

                <div class="form-field-group">
                  <label>Order URL / Commerce</label>
                  <input 
                    type="text" 
                    v-model="branch.commerce_site" 
                    class="form-input" 
                    placeholder="https://shop..."
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Logo Sidebar -->
        <div class="layout-side">
          <div class="form-section-card sticky-box">
            <div class="content-block no-border">
              <div class="block-header">
                <i class="fas fa-image"></i>
                <div class="header-info">
                  <h3>Branch Assets</h3>
                  <p>Location-specific branding</p>
                </div>
              </div>

              <div class="asset-upload-area">
                <div class="logo-preview-container">
                  <o-image-input 
                    name="logo" 
                    id="logo" 
                    :imageurl="logoPreview" 
                    @selectImage="handleLogoUpload"
                  />
                </div>
                <div class="asset-info">
                  <span class="asset-title">Branch Logo</span>
                  <p>PNG or JPG recommended. Transparent background works best. (Max 5MB)</p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isEditMode" class="form-section-card mt-4">
            <div class="content-block no-border">
              <div class="block-header">
                <i class="fas fa-info-circle"></i>
                <div class="header-info">
                  <h3>Quick Information</h3>
                  <p>Branch system summary</p>
                </div>
              </div>
              <div class="status-summary">
                <div class="summary-item">
                  <span class="label">ID Reference</span>
                  <span class="value">{{ branch.id }}</span>
                </div>
                <div class="summary-item">
                  <span class="label">Corporate</span>
                  <span class="value">{{ currentTenantName }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Loading Overlay -->
    <div v-if="isLoading" class="form-loader">
      <div class="loader-content">
        <div class="spinner-border text-danger" role="status"></div>
        <p>Updating branch repository...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import store from '@/store'
import { OSelect, OImageInput } from '@/components/OmnihControl'
import { isSupportedCountry, getCountryCallingCode } from 'libphonenumber-js'
import { getCode } from 'country-list'
import { useToast } from '@/composables/useToast'

export default {
  name: 'ExtranetBranchForm',

  components: {
    OSelect,
    OImageInput
  },

  setup() {
    const router = useRouter()
    const route = useRoute()
    const { showToast } = useToast()

    const isLoading = ref(false)
    const validation = ref({})
    const errorMessage = ref('')
    const logoPreview = ref('')

    const branch = reactive({
      id: '',
      corporate_id: '',
      code: '',
      name: '',
      logo: '',
      address: '',
      state: '',
      country: 0,
      phone: '',
      fax: '',
      website: '',
      commerce_site: '',
      city: '',
      postcode: ''
    })

    const options = ref([])
    const countries = ref([])
    const countryCode = ref('')
    const unknownCountry = ref(false)

    const isEditMode = computed(() => !!route.params.id)
    const currentTenantName = computed(() => store.state.extranet.selectedTenant?.name || '-')

    onMounted(async () => {
      isLoading.value = true
      await loadCountries()
      if (isEditMode.value) {
        await loadState(route.params.id)
      } else {
        loadCorporate()
      }
      isLoading.value = false
    })

    async function loadCountries() {
      try {
        const response = await store.dispatch('Countries/read')
        countries.value = response
        options.value = response.map(country => ({ value: country.id, text: country.name }))
      } catch (err) {
        errorMessage.value = 'Critical dependency failed: Country list unavailable.'
      }
    }

    function loadCorporate() {
      store.dispatch('Auth/getBusinessProfile').then((bp) => {
        branch.corporate_id = bp.corporate_id
      })
    }

    async function loadState(id) {
      try {
        const data = await store.dispatch('ExtranetBranch/read_single', id)
        branch.id = data.id
        branch.code = data.code
        branch.name = data.name
        branch.corporate_id = data.corporate_id
        branch.country = data.business_profile.country
        branch.address = data.business_profile.address
        branch.state = data.business_profile.state
        branch.city = data.business_profile.city
        branch.postcode = data.business_profile.postcode
        branch.fax = data.business_profile.fax
        branch.website = data.business_profile.website
        branch.commerce_site = data.business_profile.commerce_site
        
        // Handle phone parsing
        branch.phone = removeCountryCode(data.business_profile.phone, data.business_profile.country)
        
        if (data.business_profile.logo?.image_url) {
          logoPreview.value = data.business_profile.logo.image_url
        }
      } catch (err) {
        showToast({ type: 'error', title: 'Loading Failed', message: 'Unable to retrieve branch details.' })
        router.push({ name: 'ExtranetBranch' })
      }
    }

    function removeCountryCode(phone, countryId) {
      if (!countries.value.length) return phone
      const country = countries.value.find(c => c.id === parseInt(countryId))
      if (country) {
        const isoCode = getCode(country.name)
        if (isSupportedCountry(isoCode)) {
          const callingCode = getCountryCallingCode(isoCode)
          const pattern = new RegExp(`^\\+${callingCode}`)
          return phone.replace(pattern, '').trim()
        }
      }
      return phone
    }

    function updatePhoneCode() {
      if (!countries.value.length || !branch.country) return
      const country = countries.value.find(c => c.id === parseInt(branch.country))
      if (country) {
        const isoCode = getCode(country.name)
        if (isSupportedCountry(isoCode)) {
          unknownCountry.value = false
          countryCode.value = `+${getCountryCallingCode(isoCode)} `
        } else {
          unknownCountry.value = true
        }
      } else {
        unknownCountry.value = true
      }
    }

    function handleLogoUpload(img) {
      branch.logo = img
      logoPreview.value = img
    }

    function validateForm() {
      validation.value = {}
      let isValid = true
      const required = ['name', 'city', 'state', 'postcode', 'country', 'phone']
      if (!isEditMode.value) required.push('code')

      required.forEach(field => {
        if (!branch[field]) {
          validation.value[field] = [`${field.replace('_', ' ')} is required`]
          isValid = false
        }
      })

      if (parseInt(branch.country) === 0) {
        validation.value.country = ['Please select a valid country']
        isValid = false
      }

      return isValid
    }

    async function handleSubmit() {
      if (!validateForm()) {
        errorMessage.value = 'Validation failed. Please correct the highlighted fields.'
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      isLoading.value = true
      const payload = { ...branch }
      payload.phone = countryCode.value + payload.phone

      try {
        if (isEditMode.value) {
          if (payload.logo === '') delete payload.logo
          await store.dispatch('ExtranetBranch/update', { data: payload })
          showToast({ type: 'success', title: 'Branch Updated', message: 'Branch information has been synchronized.' })
        } else {
          await store.dispatch('ExtranetBranch/create', { data: payload })
          showToast({ type: 'success', title: 'Branch Registered', message: 'New location has been added to the network.' })
        }
        router.push({ name: 'ExtranetBranch' })
      } catch (err) {
        if (err.response?.data?.error) {
          try {
            validation.value = JSON.parse(err.response.data.error)
          } catch (e) {
            errorMessage.value = err.response.data.message || 'API rejected the request.'
          }
        } else {
          errorMessage.value = err.response?.data?.message || 'Failed to communicate with service.'
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } finally {
        isLoading.value = false
      }
    }

    function handleCancel() {
      router.push({ name: 'ExtranetBranch' })
    }

    watch(() => branch.country, updatePhoneCode)

    return {
      branch, isLoading, validation, errorMessage, logoPreview,
      options, isEditMode, currentTenantName, countryCode, unknownCountry,
      handleLogoUpload, handleSubmit, handleCancel
    }
  }
}
</script>

<style scoped>
.branch-form-container {
  min-height: 100vh;
}

.form-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Banners */
.error-banner {
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: var(--extranet-rounded);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
}

.banner-icon { color: #ef4444; font-size: 18px; }
.banner-text { flex: 1; color: #b91c1c; font-size: 14px; font-weight: 500; }
.banner-close { background: transparent; border: none; color: #991b1b; cursor: pointer; opacity: 0.6; }

/* Grid Layout */
.form-grid-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;
  align-items: start;
}

.form-section-card {
  background: white;
  border-radius: var(--extranet-rounded);
  border: 1px solid var(--extranet-accent);
  padding: 40px;
}

.sticky-box { position: sticky; top: 32px; }

/* Blocks */
.content-block {
  padding-bottom: 40px;
  margin-bottom: 40px;
  border-bottom: 1px solid #f1f5f9;
}

.content-block.no-border { padding-bottom: 0; margin-bottom: 0; border-bottom: none; }

.block-header { display: flex; gap: 16px; margin-bottom: 24px; }
.block-header i {
  width: 42px; height: 42px;
  background: #fff5f5; color: var(--extranet-primary);
  border-radius: var(--extranet-rounded);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}

.header-info h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
.header-info p { font-size: 13px; color: #64748b; margin: 0; }

/* Inputs */
.fields-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
.form-field-group { display: flex; flex-direction: column; gap: 8px; }
.form-field-group.full { grid-column: 1 / -1; }
.form-field-group label { font-size: 13px; font-weight: 700; color: #475569; }
.required { color: var(--extranet-primary); }

.form-input, .form-textarea {
  padding: 12px 16px;
  border: 1px solid var(--extranet-accent);
  border-radius: var(--extranet-rounded);
  font-size: 14px; color: #1e293b; transition: all 0.2s; width: 100%;
}

.form-input:focus, .form-textarea:focus {
  outline: none; border-color: var(--extranet-primary);
  box-shadow: 0 0 0 3px rgba(181, 26, 31, 0.05);
}

.form-input:disabled { background: #f8fafc; cursor: not-allowed; color: #94a3b8; }
.field-hint { font-size: 12px; color: #94a3b8; }
.error-msg { font-size: 12px; color: #ef4444; font-weight: 500; }

/* Prefix Group */
.field-prefix-group { display: flex; align-items: stretch; }
.field-prefix-group .form-input { border-top-left-radius: 0; border-bottom-left-radius: 0; }
.prefix-text {
  padding: 12px 16px; background: #f1f5f9;
  border: 1px solid var(--extranet-accent); border-right: none;
  border-top-left-radius: var(--extranet-rounded);
  border-bottom-left-radius: var(--extranet-rounded);
  color: #64748b; font-size: 14px; font-weight: 600;
  display: flex; align-items: center;
}

/* Asset Sidebar */
.asset-upload-area {
  padding: 24px;
  background: #f8fafc;
  border-radius: var(--extranet-rounded);
  text-align: center;
}

.logo-preview-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

:deep(.o-image-input),
:deep(.o-image-input .image-preview),
:deep(.o-image-input .upload-placeholder) {
  border-radius: var(--extranet-rounded) !important;
}

.asset-info { text-align: center; }
.asset-title { display: block; font-size: 14px; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
.asset-info p { font-size: 12px; color: #94a3b8; margin: 0; line-height: 1.6; }

/* Status Summary */
.status-summary { display: flex; flex-direction: column; gap: 12px; }
.summary-item { display: flex; justify-content: space-between; align-items: center; }
.summary-item .label { font-size: 13px; color: #64748b; font-weight: 500; }
.summary-item .value { font-size: 13px; color: #1e293b; font-weight: 700; }

/* Loader */
.form-loader {
  position: fixed; inset: 0; background: rgba(255,255,255,0.7);
  backdrop-filter: blur(4px); display: flex; align-items: center;
  justify-content: center; z-index: 1000;
}

.loader-content { text-align: center; }
.loader-content p { margin-top: 16px; font-weight: 600; color: #475569; }

/* Common UI adjustments */
.btn-primary, .btn-secondary {
  padding: 10px 20px; border-radius: var(--extranet-rounded);
  font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s;
  display: inline-flex; align-items: center; border: none;
}

.btn-primary {
  background: var(--extranet-primary); color: white;
  box-shadow: 0 4px 12px rgba(181, 26, 31, 0.15);
}

.btn-primary:hover:not(:disabled) { filter: brightness(1.1); transform: translateY(-1px); }

.btn-secondary {
  background: white; color: #475569; border: 1px solid var(--extranet-accent);
}

.btn-secondary:hover { background: #f8fafc; color: #1e293b; }

.text-dark { color: #1e293b; }

@media (max-width: 1024px) {
  .form-grid-layout { grid-template-columns: 1fr; }
  .sticky-box { position: static; }
}
</style>
